import React,{useState,useRef,useEffect,useCallback} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import JSZip from 'jszip';
import SafeIcon from './common/SafeIcon';
import PricingModal from './components/PricingModal';

const {FiUpload,FiDownload,FiImage,FiSliders,FiCheck,FiAlertCircle,FiX,FiEye,FiEyeOff,FiMaximize,FiLayers,FiChevronLeft,FiChevronRight,FiZoomIn,FiZoomOut,FiRotateCcw,FiPlus,FiCornerUpLeft,FiCornerUpRight,FiTrash2,FiCommand,FiSettings,FiGlobe,FiLoader,FiScissors,FiInfo,FiCreditCard}=FiIcons;
const {BsEyedropper,BsEraser,BsMagic,BsScissors}=BsIcons;

// --- Translations --- 
const translations={jp: {brand: "LINEスタンプ制作",subtitle: "背景透過ツール",editor: "背景きりぬき",history: "履歴",gallery: "画像リスト",files: "枚のファイル",colorSelection: "カラー抽出",auto: "自動",noColors: "色が選択されていません...",eyeDropper: "スポイト",eraser: "消しゴム",eraserSettings: "消しゴム設定",brushSize: "ブラシサイズ",undo: "元に戻す",redo: "やり直し",advanced: "細かい修正",edgeCorrection: "エッジの色被り補正",edgeDesc: "境界線の雑味を除去",intensity: "補正の強度",threshold: "しきい値",softness: "境界のぼかし",export: "PNGで保存",batchExport: "一括保存",saveCurrent: "現在の画像を保存",preview: "プレビュー",processing: "処理中...",pleaseWait: "お待ちください",baseImage: "元画像",compare: "比較",viewingOriginal: "元画像を表示中",dropZoneTitle: "ここに画像をドロップ",dropZoneDesc: "PNG,JPG,WEBP に対応（複数選択可）",clickBrowse: "クリックしてファイルを選択",pan: "移動",zoom: "ズーム",fileNameModalTitle: "保存設定",batchFileNameModalTitle: "一括保存の設定",fileNamePlaceholder: "ファイル名",cancel: "キャンセル",save: "保存",done: "完了",numberSuffix: "（連番が付与されます）",saveSuccess: "保存が完了しました！",saveHint: "保存先はブラウザの「ダウンロード」フォルダ、または選択した場所を確認してください。",saveAsChoice: "保存先を毎回選択する（対応ブラウザのみ）",pricing: "プラン"},en: {brand: "LINE Sticker Studio",subtitle: "Background Remover",editor: "Cutout",history: "History",gallery: "Gallery",files: "FILES",colorSelection: "Color Selection",auto: "Auto",noColors: "No colors selected...",eyeDropper: "Eye Dropper",eraser: "Manual Eraser",eraserSettings: "Eraser Settings",brushSize: "Brush Size",undo: "Undo",redo: "Redo",advanced: "Fine Tuning",edgeCorrection: "Edge Correction",edgeDesc: "Removes fringe colors",intensity: "Intensity",threshold: "Color Threshold",softness: "Mask Softness",export: "Save as PNG",batchExport: "Batch Export",saveCurrent: "Save Current",preview: "Preview",processing: "Processing...",pleaseWait: "Please wait",baseImage: "Base Image",compare: "Compare",viewingOriginal: "Viewing Original",dropZoneTitle: "Drop your images here",dropZoneDesc: "Support PNG,JPG,WEBP and multiple uploads",clickBrowse: "Click to browse files",pan: "Pan",zoom: "Zoom",fileNameModalTitle: "Save Settings",batchFileNameModalTitle: "Batch Export Settings",fileNamePlaceholder: "filename",cancel: "Cancel",save: "Save",done: "Done",numberSuffix: "(Sequential numbers will be added)",saveSuccess: "Save Completed!",saveHint: "Please check your 'Downloads' folder or the selected location.",saveAsChoice: "Choose location for every save (supported browsers)",pricing: "Pricing"}};

const ControlSection=({title,icon,children,badge})=> (
  <section className="bg-white/70 backdrop-blur-md rounded-3xl p-5 border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
          <SafeIcon icon={icon} size={18} />
        </div>
        <h3 className="font-bold text-slate-800 text-sm tracking-tight">{title}</h3>
      </div>
      {badge && (
        <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full uppercase tracking-wider">
          {badge}
        </span>
      )}
    </div>
    {children}
  </section>
);

const CustomSlider=({label,value,min,max,onChange,icon})=> (
  <div className="space-y-3 group">
    <div className="flex justify-between items-center">
      <label className="text-xs font-semibold text-slate-600 flex items-center gap-2 transition-colors group-hover:text-indigo-600">
        {icon && <SafeIcon icon={icon} size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />}
        {label}
      </label>
      <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-lg border border-indigo-100 shadow-sm transition-all group-hover:bg-indigo-100 scale-100 group-hover:scale-105">
        {value}
      </span>
    </div>
    <input type="range" min={min} max={max} value={value} onChange={(e)=> onChange(Number(e.target.value))} className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600 hover:accent-indigo-500 transition-all active:scale-[0.99]" />
  </div>
);

export default function App() {
const [lang,setLang]=useState('jp');
const t=translations[lang];
const [images,setImages]=useState([]);
const [currentIndex,setCurrentIndex]=useState(0);
const currentImageObj=images[currentIndex]?.imgObj;
const currentFileName=images[currentIndex]?.name;
const [targetColors,setTargetColors]=useState([]);
const [threshold,setThreshold]=useState(60);
const [softness,setSoftness]=useState(30);
const [fringeIntensity,setFringeIntensity]=useState(40);
const [eraserSize,setEraserSize]=useState(40);
const [removeFringe,setRemoveFringe]=useState(true);
const [isProcessing,setIsProcessing]=useState(false);
const [isDragging,setIsDragging]=useState(false);
const [isSaveModalOpen,setIsSaveModalOpen]=useState(false);
const [isBatchSaveModalOpen,setIsBatchSaveModalOpen]=useState(false);
const [isPricingOpen,setIsPricingOpen]=useState(false);
const [customFileName,setCustomFileName]=useState('');
const [batchFileName,setBatchFileName]=useState('');
const [useSavePicker,setUseSavePicker]=useState(true);
const [toast,setToast]=useState(null);
const [mode,setMode]=useState('none');
const [showOriginal,setShowOriginal]=useState(false);
const [zoom,setZoom]=useState(1);
const [pan,setPan]=useState({x: 0,y: 0});
const isPanning=useRef(false);
const lastPanPos=useRef({x: 0,y: 0});
const initialPinchDistance=useRef(null);
const initialPinchZoom=useRef(1);
const [cursorPos,setCursorPos]=useState({x: -100,y: -100,scale: 1});
const [showCursor,setShowCursor]=useState(false);
const [activeSheet,setActiveSheet]=useState(null);
const canvasRef=useRef(null);
const eraserCanvasRef=useRef(null);
const fileInputRef=useRef(null);
const isDrawing=useRef(false);

const showToast=(message)=> {setToast(message);setTimeout(()=> setToast(null),5000);};

const detectBackgroundColor=(img)=> {
  const tempCanvas=document.createElement('canvas');
  const tempCtx=tempCanvas.getContext('2d');
  tempCanvas.width=img.width;
  tempCanvas.height=img.height;
  tempCtx.drawImage(img,0,0);
  const samples=[
    tempCtx.getImageData(0,0,1,1).data,
    tempCtx.getImageData(img.width - 1,0,1,1).data,
    tempCtx.getImageData(0,img.height - 1,1,1).data,
    tempCtx.getImageData(img.width - 1,img.height - 1,1,1).data
  ];
  const detectedColors=[];
  samples.forEach(s=> {
    const color={r: s[0],g: s[1],b: s[2]};
    if (s[3] > 50) {
      const isDuplicate=detectedColors.some(c=> Math.abs(c.r - color.r) < 10 && Math.abs(c.g - color.g) < 10 && Math.abs(c.b - color.b) < 10 );
      if (!isDuplicate) detectedColors.push(color);
    }
  });
  if (detectedColors.length > 0) {
    setTargetColors(detectedColors.slice(0,2));
  }
};

const loadEraserDataUrl=useCallback((dataUrl)=> {
  const canvas=eraserCanvasRef.current;
  if (!canvas) return;
  const ctx=canvas.getContext('2d');
  ctx.clearRect(0,0,canvas.width,canvas.height);
  if (dataUrl) {
    const img=new Image();
    img.onload=()=> ctx.drawImage(img,0,0);
    img.src=dataUrl;
  }
},[]);

const processFiles=(files)=> {
  if (files.length===0) return;
  const isFirstUpload=images.length===0;
  if (eraserCanvasRef.current && currentImageObj) {
    const currentDataUrl=eraserCanvasRef.current.toDataURL();
    setImages(prev=> {
      const newImages=[...prev];
      if (newImages[currentIndex]) newImages[currentIndex].eraserDataUrl=currentDataUrl;
      return newImages;
    });
  }
  const newImagesPromises=files.map(file=> {
    return new Promise((resolve)=> {
      const reader=new FileReader();
      reader.onload=(event)=> {
        const img=new Image();
        img.onload=()=> {
          resolve({id: Math.random().toString(36).substr(2,9),name: file.name,imgObj: img,eraserDataUrl: null,history: [null],historyStep: 0});
        };
        img.src=event.target.result;
      };
      reader.readAsDataURL(file);
    });
  });
  Promise.all(newImagesPromises).then(newImages=> {
    if (isFirstUpload && newImages.length > 0) {
      detectBackgroundColor(newImages[0].imgObj);
    }
    setImages(prev=> {
      const updated=[...prev,...newImages];
      if (prev.length===0) setCurrentIndex(0);
      else setCurrentIndex(prev.length);
      return updated;
    });
    setShowOriginal(false);
    setMode('none');
    handleResetZoom();
    if (window.innerWidth < 1024) setActiveSheet(null);
  });
  if (fileInputRef.current) fileInputRef.current.value='';
};

const handleImageUpload=(e)=> {if (e.target.files) processFiles(Array.from(e.target.files));};
const handleDragOver=(e)=> {e.preventDefault();setIsDragging(true);};
const handleDragLeave=(e)=> {e.preventDefault();setIsDragging(false);};
const handleDrop=(e)=> {e.preventDefault();setIsDragging(false);if (e.dataTransfer.files) processFiles(Array.from(e.dataTransfer.files));};

const handleSelectImage=(index)=> {
  if (index===currentIndex || index < 0 || index >=images.length) return;
  if (eraserCanvasRef.current) {
    const dataUrl=eraserCanvasRef.current.toDataURL();
    setImages(prev=> {
      const newImages=[...prev];
      newImages[currentIndex]={...newImages[currentIndex],eraserDataUrl: dataUrl};
      return newImages;
    });
  }
  setCurrentIndex(index);
  setShowOriginal(false);
  setShowCursor(false);
  setMode('none');
  handleResetZoom();
  if (window.innerWidth < 1024 && activeSheet==='gallery') setActiveSheet(null);
};

// Keyboard navigation for arrow keys
useEffect(()=> {
  const handleKeyDown=(e)=> {
    if (images.length <=1 || isSaveModalOpen || isBatchSaveModalOpen || isPricingOpen) return;
    const target=e.target;
    if (target.tagName==='INPUT' || target.tagName==='TEXTAREA') return;
    if (e.key==='ArrowLeft') {e.preventDefault();handleSelectImage(currentIndex - 1);}
    else if (e.key==='ArrowRight') {e.preventDefault();handleSelectImage(currentIndex + 1);}
  };
  window.addEventListener('keydown',handleKeyDown);
  return ()=> window.removeEventListener('keydown',handleKeyDown);
},[currentIndex,images.length,isSaveModalOpen,isBatchSaveModalOpen,isPricingOpen]);

const handleRemoveImage=(e,indexToRemove)=> {
  e.stopPropagation();
  setImages(prev=> prev.filter((_,i)=> i !==indexToRemove));
  if (currentIndex===indexToRemove) {
    setCurrentIndex(Math.max(0,indexToRemove - 1));
    setShowOriginal(false);
    setMode('none');
    handleResetZoom();
  } else if (currentIndex > indexToRemove) {
    setCurrentIndex(currentIndex - 1);
  }
};

useEffect(()=> {
  if (currentImageObj && canvasRef.current && eraserCanvasRef.current) {
    canvasRef.current.width=currentImageObj.width;
    canvasRef.current.height=currentImageObj.height;
    eraserCanvasRef.current.width=currentImageObj.width;
    eraserCanvasRef.current.height=currentImageObj.height;
    const ctx=eraserCanvasRef.current.getContext('2d');
    ctx.lineCap='round';
    ctx.lineJoin='round';
    loadEraserDataUrl(images[currentIndex]?.eraserDataUrl);
  }
},[currentIndex,currentImageObj,loadEraserDataUrl]);

useEffect(()=> {
  if (!currentImageObj || !canvasRef.current) return;
  setIsProcessing(true);
  const timeoutId=setTimeout(()=> {
    const canvas=canvasRef.current;
    const ctx=canvas.getContext('2d',{willReadFrequently: true});
    ctx.clearRect(0,0,canvas.width,canvas.height);
    ctx.drawImage(currentImageObj,0,0);
    if (showOriginal || targetColors.length===0) {setIsProcessing(false);return;}
    const imageData=ctx.getImageData(0,0,canvas.width,canvas.height);
    const data=imageData.data;
    for (let i=0;i < data.length;i +=4) {
      const r=data[i],g=data[i + 1],b=data[i + 2];
      let minDistance=Infinity;
      let closestColor=targetColors[0];
      for (const tColor of targetColors) {
        const dist=Math.sqrt(Math.pow(r - tColor.r,2) + Math.pow(g - tColor.g,2) + Math.pow(b - tColor.b,2));
        if (dist < minDistance) {minDistance=dist;closestColor=tColor;}
      }
      if (minDistance < threshold) {
        data[i + 3]=0;
      } else if (minDistance < threshold + softness) {
        const s=Math.max(softness,1);
        data[i + 3]=Math.min(data[i + 3],Math.max(0,Math.min(255,((minDistance - threshold) / s) * 255)));
        if (removeFringe && data[i + 3] > 0) {
          let r_val=r,g_val=g,b_val=b;
          if (closestColor.g >=closestColor.r && closestColor.g >=closestColor.b) {
            let maxRB=Math.max(r,b);if (g > maxRB) g_val=maxRB;
          } else if (closestColor.b >=closestColor.r && closestColor.b >=closestColor.g) {
            let maxRG=Math.max(r,g);if (b > maxRG) b_val=maxRG;
          } else {
            let maxGB=Math.max(g,b);if (r > maxGB) r_val=maxGB;
          }
          data[i]=r_val;data[i + 1]=g_val;data[i + 2]=b_val;
          if (data[i + 3] < 255) {
            const intensityFactor=1 - (fringeIntensity / 100);
            data[i + 3]=data[i + 3] * intensityFactor;
          }
        }
      }
    }
    ctx.putImageData(imageData,0,0);
    setIsProcessing(false);
  },10);
  return ()=> clearTimeout(timeoutId);
},[currentImageObj,targetColors,threshold,softness,fringeIntensity,removeFringe,showOriginal]);

const getCanvasCoordinates=(clientX,clientY,canvas)=> {
  const rect=canvas.getBoundingClientRect();
  const scale=canvas.width / rect.width;
  const x=(clientX - rect.left) * scale;
  const y=(clientY - rect.top) * scale;
  return {x,y,scale};
};

const handleContainerPointerDown=(e)=> {
  if (e.target.closest('button,input,[role="button"]')) return;
  if (mode !=='none' || !currentImageObj) return;
  if (e.pointerType==='touch' && !e.isPrimary) return;
  isPanning.current=true;
  lastPanPos.current={x: e.clientX,y: e.clientY};
  try {e.currentTarget.setPointerCapture(e.pointerId);} catch (err) {console.debug(err);}
};

const handleContainerPointerMove=(e)=> {
  if (!isPanning.current || mode !=='none') return;
  if (e.pointerType==='touch' && initialPinchDistance.current !==null) return;
  const dx=e.clientX - lastPanPos.current.x;
  const dy=e.clientY - lastPanPos.current.y;
  setPan(prev=> ({x: prev.x + dx,y: prev.y + dy}));
  lastPanPos.current={x: e.clientX,y: e.clientY};
};

const handleContainerPointerUp=(e)=> {
  if (isPanning.current) {
    isPanning.current=false;
    try {e.currentTarget.releasePointerCapture(e.pointerId);} catch (err) {console.debug(err);}
  }
};

const handleTouchStart=(e)=> {
  if (e.touches.length===2 && mode==='none') {
    initialPinchDistance.current=Math.sqrt(
      Math.pow(e.touches[0].clientX - e.touches[1].clientX,2) + Math.pow(e.touches[0].clientY - e.touches[1].clientY,2)
    );
    initialPinchZoom.current=zoom;
  }
};

const handleTouchMove=(e)=> {
  if (e.touches.length===2 && initialPinchDistance.current !==null && mode==='none') {
    const currentDistance=Math.sqrt(
      Math.pow(e.touches[0].clientX - e.touches[1].clientX,2) + Math.pow(e.touches[0].clientY - e.touches[1].clientY,2)
    );
    const scale=currentDistance / initialPinchDistance.current;
    const newZoom=Math.min(Math.max(initialPinchZoom.current * scale,0.2),5);
    setZoom(newZoom);
  }
};

const handleTouchEnd=()=> {initialPinchDistance.current=null;};
const handleZoomIn=()=> setZoom(z=> Math.min(z + 0.2,5));
const handleZoomOut=()=> setZoom(z=> Math.max(z - 0.2,0.2));
const handleResetZoom=()=> {setZoom(1);setPan({x: 0,y: 0});};
const handleWheel=(e)=> {if (mode !=='none' || !currentImageObj) return;if (e.deltaY < 0) setZoom(z=> Math.min(z + 0.1,5));else setZoom(z=> Math.max(z - 0.1,0.2));};

const handleCanvasPointerMove=(e)=> {
  const canvas=eraserCanvasRef.current;
  if (!canvas) return;
  if (mode==='eraser' || mode==='picker') {
    const rect=canvas.getBoundingClientRect();
    const scale=canvas.width / rect.width;
    setCursorPos({x: e.clientX - rect.left,y: e.clientY - rect.top,scale: scale});
    setShowCursor(true);
  }
  if (isDrawing.current && mode==='eraser') {
    const ctx=canvas.getContext('2d');
    const {x,y}=getCanvasCoordinates(e.clientX,e.clientY,canvas);
    ctx.lineTo(x,y);
    ctx.stroke();
  }
};

const handleCanvasPointerDown=(e)=> {
  if (!currentImageObj || !eraserCanvasRef.current) return;
  if (showOriginal) return;
  const canvas=eraserCanvasRef.current;
  try {e.target.setPointerCapture(e.pointerId);} catch (err) {console.debug(err);}
  if (mode==='picker') {
    const {x,y}=getCanvasCoordinates(e.clientX,e.clientY,canvas);
    if (x >=0 && x < canvas.width && y >=0 && y < canvas.height) {
      const tempCanvas=document.createElement('canvas');
      tempCanvas.width=currentImageObj.width;
      tempCanvas.height=currentImageObj.height;
      const tCtx=tempCanvas.getContext('2d');
      tCtx.drawImage(currentImageObj,0,0);
      const pixelData=tCtx.getImageData(x,y,1,1).data;
      const newColor={r: pixelData[0],g: pixelData[1],b: pixelData[2]};
      const isDuplicate=targetColors.some(c=> c.r===newColor.r && c.g===newColor.g && c.b===newColor.b);
      if (!isDuplicate) setTargetColors([...targetColors,newColor]);
      setMode('none');
      setShowCursor(false);
    }
  } else if (mode==='eraser') {
    isDrawing.current=true;
    const ctx=canvas.getContext('2d');
    const {x,y}=getCanvasCoordinates(e.clientX,e.clientY,canvas);
    ctx.lineWidth=eraserSize;
    ctx.strokeStyle='rgba(255,0,0,0.8)';
    ctx.beginPath();
    ctx.moveTo(x,y);
    ctx.lineTo(x,y);
    ctx.stroke();
    setShowCursor(true);
  }
};

const saveHistory=()=> {
  if (!eraserCanvasRef.current) return;
  const currentDataUrl=eraserCanvasRef.current.toDataURL();
  setImages(prev=> {
    const newImages=[...prev];
    const img=newImages[currentIndex];
    if (!img) return prev;
    const newHistory=img.history.slice(0,img.historyStep + 1);
    newHistory.push(currentDataUrl);
    newImages[currentIndex]={...img,eraserDataUrl: currentDataUrl,history: newHistory,historyStep: newHistory.length - 1};
    return newImages;
  });
};

const stopDrawing=(e)=> {
  if (isDrawing.current) {
    isDrawing.current=false;
    if (eraserCanvasRef.current) {
      eraserCanvasRef.current.getContext('2d').closePath();
      if (e?.pointerId) {
        try {eraserCanvasRef.current.releasePointerCapture(e.pointerId);} catch (err) {console.debug(err);}
      }
      saveHistory();
    }
  }
  if (e?.pointerType==='touch') setShowCursor(false);
};

const handleUndo=()=> {
  setImages(prev=> {
    const img=prev[currentIndex];
    if (img && img.historyStep > 0) {
      const newStep=img.historyStep - 1;
      const previousDataUrl=img.history[newStep];
      loadEraserDataUrl(previousDataUrl);
      const newImages=[...prev];
      newImages[currentIndex]={...img,eraserDataUrl: previousDataUrl,historyStep: newStep};
      return newImages;
    }
    return prev;
  });
};

const handleRedo=()=> {
  setImages(prev=> {
    const img=prev[currentIndex];
    if (img && img.historyStep < img.history.length - 1) {
      const newStep=img.historyStep + 1;
      const nextDataUrl=img.history[newStep];
      loadEraserDataUrl(nextDataUrl);
      const newImages=[...prev];
      newImages[currentIndex]={...img,eraserDataUrl: nextDataUrl,historyStep: newStep};
      return newImages;
    }
    return prev;
  });
};

const removeTargetColor=(index)=> {const newColors=[...targetColors];newColors.splice(index,1);setTargetColors(newColors);};

const toggleMode=(targetMode)=> {
  if (mode===targetMode) {setMode('none');setShowCursor(false);}
  else {setMode(targetMode);setShowOriginal(false);if (window.innerWidth < 1024) setActiveSheet(null);}
};

const toggleShowOriginal=()=> {
  const nextVal=!showOriginal;
  setShowOriginal(nextVal);
  if (nextVal && mode !=='none') {setMode('none');setShowCursor(false);}
};

const processForDownload=async (imgItem,isCurrent)=> {
  return new Promise((resolve)=> {
    const canvas=document.createElement('canvas');
    canvas.width=imgItem.imgObj.width;
    canvas.height=imgItem.imgObj.height;
    const ctx=canvas.getContext('2d');
    ctx.drawImage(imgItem.imgObj,0,0);
    if (targetColors.length > 0) {
      const imageData=ctx.getImageData(0,0,canvas.width,canvas.height);
      const data=imageData.data;
      for (let i=0;i < data.length;i +=4) {
        const r=data[i],g=data[i + 1],b=data[i + 2];
        let minDistance=Infinity;
        let closestColor=targetColors[0];
        for (const tColor of targetColors) {
          const dist=Math.sqrt(Math.pow(r - tColor.r,2) + Math.pow(g - tColor.g,2) + Math.pow(b - tColor.b,2));
          if (dist < minDistance) {minDistance=dist;closestColor=tColor;}
        }
        if (minDistance < threshold) {
          data[i + 3]=0;
        } else if (minDistance < threshold + softness) {
          const s=Math.max(softness,1);
          data[i + 3]=Math.min(data[i + 3],Math.max(0,Math.min(255,((minDistance - threshold) / s) * 255)));
          if (removeFringe && data[i + 3] > 0) {
            if (closestColor.g >=closestColor.r && closestColor.g >=closestColor.b) data[i + 1]=Math.min(g,Math.max(r,b));
            else if (closestColor.b >=closestColor.r && closestColor.b >=closestColor.g) data[i + 2]=Math.min(b,Math.max(r,g));
            else data[i]=Math.min(r,Math.max(g,b));
            if (data[i + 3] < 255) {
              const intensityFactor=1 - (fringeIntensity / 100);
              data[i + 3]=data[i + 3] * intensityFactor;
            }
          }
        }
      }
      ctx.putImageData(imageData,0,0);
    }
    const currentEraserUrl=isCurrent ? eraserCanvasRef.current?.toDataURL() : imgItem.eraserDataUrl;
    if (currentEraserUrl) {
      const eraserImg=new Image();
      eraserImg.onload=()=> {ctx.globalCompositeOperation='destination-out';ctx.drawImage(eraserImg,0,0);resolve(canvas.toDataURL('image/png'));};
      eraserImg.src=currentEraserUrl;
    } else {resolve(canvas.toDataURL('image/png'));}
  });
};

const openSaveModal=()=> {
  if (!currentImageObj) return;
  const defaultName=currentFileName ? currentFileName.replace(/\.[^/.]+$/,"") : 'image';
  setCustomFileName(defaultName);
  setIsSaveModalOpen(true);
};

const confirmDownloadSingle=async ()=> {
  setIsSaveModalOpen(false);
  setIsProcessing(true);
  const dataUrl=await processForDownload(images[currentIndex],true);
  const fileName=`${customFileName || 'image'}.png`;
  if (useSavePicker && 'showSaveFilePicker' in window) {
    try {
      const handle=await window.showSaveFilePicker({suggestedName: fileName,types: [{description: 'PNG Image',accept: {'image/png': ['.png']}}],});
      const writable=await handle.createWritable();
      const response=await fetch(dataUrl);
      const blob=await response.blob();
      await writable.write(blob);
      await writable.close();
      showToast(t.saveSuccess);
    } catch (err) {
      console.log("Picker cancelled or not supported,using fallback.");
      if (err.name !=='AbortError') triggerDirectDownload(dataUrl,fileName);
    }
  } else {triggerDirectDownload(dataUrl,fileName);}
  setIsProcessing(false);
};

const triggerDirectDownload=(dataUrl,fileName)=> {
  const link=document.createElement('a');
  link.download=fileName;
  link.href=dataUrl;
  link.click();
  showToast(t.saveSuccess);
};

const openBatchSaveModal=()=> {if (images.length===0) return;setBatchFileName('stamp');setIsBatchSaveModalOpen(true);};

const confirmDownloadBatch=async ()=> {
  setIsBatchSaveModalOpen(false);
  setIsProcessing(true);
  
  const zip = new JSZip();
  const prefix = batchFileName || 'image';
  
  try {
    // Process all images and add to ZIP
    for (let i = 0; i < images.length; i++) {
      const dataUrl = await processForDownload(images[i], i === currentIndex);
      // Remove data:image/png;base64, prefix for JSZip
      const base64Data = dataUrl.split(',')[1];
      zip.file(`${prefix}_${i + 1}.png`, base64Data, {base64: true});
    }

    // Generate ZIP file
    const content = await zip.generateAsync({type: "blob"});
    const zipFileName = `${prefix}_batch.zip`;

    // Download ZIP
    const link = document.createElement('a');
    link.href = URL.createObjectURL(content);
    link.download = zipFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    showToast(`${images.length} ${t.files} ${t.saveSuccess}`);
  } catch (err) {
    console.error("Batch download failed:", err);
    showToast("Error during batch download");
  } finally {
    setIsProcessing(false);
  }
};

const checkerboardStyle={backgroundColor: '#ffffff',backgroundImage: `linear-gradient(45deg,#f1f5f9 25%,transparent 25%),linear-gradient(-45deg,#f1f5f9 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f1f5f9 75%),linear-gradient(-45deg,transparent 75%,#f1f5f9 75%)`,backgroundSize: '16px 16px',backgroundPosition: '0 0,0 8px,8px -8px,-8px 0px'};
const originalBgStyle={backgroundColor: '#ffffff'};

let cursorClass='';
if (!currentImageObj) cursorClass='cursor-pointer hover:bg-slate-200/50';
else if (showOriginal) cursorClass='cursor-default';
else if (mode==='picker' || mode==='eraser') cursorClass='cursor-none';
else cursorClass=isPanning.current ? 'cursor-grabbing' : 'cursor-grab';

const renderGallery=(idPrefix)=> (
  <AnimatePresence>
    {images.length > 0 && (
      <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} className="bg-white/70 backdrop-blur-md rounded-3xl p-5 border border-slate-200/50 shadow-sm" >
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <SafeIcon icon={FiImage} size={16} className="text-indigo-500" />
            {t.gallery}
          </h3>
          <span className="text-[10px] font-black bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">{images.length} {t.files}</span>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide items-center">
          <button onClick={()=> fileInputRef.current?.click()} className="w-14 h-14 shrink-0 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-500 hover:border-indigo-400 hover:bg-indigo-50 transition-all group active:scale-95" >
            <SafeIcon icon={FiPlus} size={20} className="group-hover:scale-110 transition-transform" />
          </button>
          {images.map((imgItem,idx)=> (
            <motion.div key={`${idPrefix}-${imgItem.id}`} layout onClick={()=> handleSelectImage(idx)} className={`relative w-14 h-14 shrink-0 rounded-2xl overflow-hidden border-2 cursor-pointer transition-all active:scale-95 ${idx===currentIndex ? 'border-indigo-500 shadow-lg shadow-indigo-200 ring-4 ring-indigo-100 scale-105' : 'border-transparent hover:border-slate-300 hover:scale-105 opacity-70 hover:opacity-100'}`} >
              <img src={imgItem.imgObj.src} className="w-full h-full object-cover" alt="thumbnail" />
              <button onClick={(e)=> handleRemoveImage(e,idx)} className="absolute top-1 right-1 bg-white/90 backdrop-blur-sm text-slate-400 rounded-lg p-0.5 hover:bg-rose-500 hover:text-white transition-all shadow-sm active:scale-90" >
                <SafeIcon icon={FiX} size={10} />
              </button>
            </motion.div>
          ))}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const renderTools=()=> (
  <div className="space-y-6">
    <ControlSection title={t.colorSelection} icon={BsEyedropper} badge={t.auto}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {targetColors.map((color,index)=> (
            <motion.div initial={{scale: 0}} animate={{scale: 1}} key={index} className="flex items-center bg-white border border-slate-200 rounded-full pl-1.5 pr-2.5 py-1 shadow-sm group hover:border-indigo-200 hover:shadow-md transition-all active:scale-95" >
              <div className="w-4 h-4 rounded-full shadow-inner border border-black/5 mr-2 transition-transform group-hover:scale-110" style={{backgroundColor: `rgb(${color.r},${color.g},${color.b})`}} />
              <button onClick={()=> removeTargetColor(index)} className="text-slate-300 hover:text-rose-500 transition-colors active:scale-90" >
                <SafeIcon icon={FiX} size={12} />
              </button>
            </motion.div>
          ))}
          {targetColors.length===0 && <p className="text-[11px] text-slate-400 py-1 font-medium italic">{t.noColors}</p>}
        </div>
        <div className="flex gap-3">
          <button onClick={()=> toggleMode('picker')} disabled={showOriginal} className={`flex-1 h-11 rounded-2xl flex items-center justify-center gap-2 font-bold text-xs transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${mode==='picker' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30 ring-2 ring-indigo-600 ring-offset-2 ring-offset-[#fcfcfd]' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 shadow-sm hover:shadow'}`} >
            <SafeIcon icon={BsEyedropper} size={14} className={mode==='picker' ? 'animate-bounce' : ''} />
            {t.eyeDropper}
          </button>
          <button onClick={()=> toggleMode('eraser')} disabled={showOriginal} className={`flex-1 h-11 rounded-2xl flex items-center justify-center gap-2 font-bold text-xs transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${mode==='eraser' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30 ring-2 ring-indigo-600 ring-offset-2 ring-offset-[#fcfcfd]' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 shadow-sm hover:shadow'}`} >
            <SafeIcon icon={BsEraser} size={14} className={mode==='eraser' ? 'animate-pulse' : ''} />
            {t.eraser}
          </button>
        </div>
      </div>
    </ControlSection>
    <AnimatePresence>
      {mode==='eraser' && (
        <motion.div initial={{opacity: 0,height: 0}} animate={{opacity: 1,height: 'auto'}} exit={{opacity: 0,height: 0}} className="overflow-hidden" >
          <ControlSection title={t.eraserSettings} icon={BsEraser}>
            <div className="space-y-4">
              <CustomSlider label={t.brushSize} value={eraserSize} min={5} max={150} onChange={setEraserSize} />
              <div className="flex items-center gap-2 pt-2">
                <button onClick={handleUndo} disabled={!images[currentIndex]?.historyStep} className="flex-1 h-10 rounded-xl bg-slate-100 text-slate-600 font-bold text-xs flex items-center justify-center gap-2 hover:bg-slate-200 disabled:opacity-30 transition-all active:scale-95" >
                  <SafeIcon icon={FiCornerUpLeft} size={14} /> {t.undo}
                </button>
                <button onClick={handleRedo} disabled={images[currentIndex]?.historyStep >=images[currentIndex]?.history.length - 1} className="flex-1 h-10 rounded-xl bg-slate-100 text-slate-600 font-bold text-xs flex items-center justify-center gap-2 hover:bg-slate-200 disabled:opacity-30 transition-all active:scale-95" >
                  <SafeIcon icon={FiCornerUpRight} size={14} /> {t.redo}
                </button>
              </div>
            </div>
          </ControlSection>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const renderAdjust=()=> (
  <ControlSection title={t.advanced} icon={BsMagic}>
    <div className="space-y-5">
      <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100/50 transition-all hover:bg-indigo-50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center shadow-sm text-indigo-600">
              <SafeIcon icon={BsMagic} size={14} />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-800">{t.edgeCorrection}</p>
              <p className="text-[10px] text-slate-500 font-medium">{t.edgeDesc}</p>
            </div>
          </div>
          <button onClick={()=> setRemoveFringe(!removeFringe)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-all active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 ${removeFringe ? 'bg-indigo-600' : 'bg-slate-200'}`} >
            <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition-transform ${removeFringe ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
        </div>
        {removeFringe && (
          <motion.div initial={{opacity: 0}} animate={{opacity: 1}}>
            <CustomSlider label={t.intensity} value={fringeIntensity} min={0} max={100} onChange={setFringeIntensity} />
          </motion.div>
        )}
      </div>
      <div className="space-y-4">
        <CustomSlider label={t.threshold} value={threshold} min={0} max={200} onChange={setThreshold} icon={FiSliders} />
        <CustomSlider label={t.softness} value={softness} min={0} max={100} onChange={setSoftness} icon={FiSliders} />
      </div>
    </div>
  </ControlSection>
);

const renderExport=()=> (
  <div className="bg-slate-900 rounded-3xl p-6 shadow-xl shadow-indigo-100 space-y-4 relative overflow-hidden group">
    <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/20 rounded-full blur-2xl group-hover:bg-indigo-500/30 transition-all duration-500" />
    <div className="flex items-center gap-3 mb-2 relative z-10">
      <div className="p-2 bg-white/10 text-white rounded-xl backdrop-blur-md">
        <SafeIcon icon={FiDownload} size={18} />
      </div>
      <h3 className="font-bold text-white text-sm">{t.export}</h3>
    </div>
    <button onClick={openBatchSaveModal} disabled={images.length===0 || isProcessing} className="w-full h-12 bg-indigo-500 hover:bg-indigo-400 disabled:bg-slate-700 text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-500/20 active:scale-95 hover:shadow-indigo-500/40 relative z-10" >
      <SafeIcon icon={FiLayers} size={16} /> {t.batchExport} ({images.length})
    </button>
    <button onClick={openSaveModal} disabled={images.length===0 || isProcessing} className="w-full h-11 bg-white/5 hover:bg-white/10 text-white/80 text-xs font-bold rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-95 relative z-10" >
      <SafeIcon icon={FiDownload} size={14} /> {t.saveCurrent}
    </button>
  </div>
);

return (
  <div className="min-h-screen bg-[#fcfcfd] text-slate-800 selection:bg-indigo-100 selection:text-indigo-900">
    <nav className="sticky top-0 z-[100] bg-white/80 backdrop-blur-xl border-b border-slate-200/60 px-4 sm:px-6 py-3 sm:py-4">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200 transition-transform hover:scale-105 hover:rotate-3 shrink-0">
            <SafeIcon icon={FiLayers} className="text-white" size={18} />
          </div>
          <div className="flex flex-col">
            <h1 className="text-sm sm:text-lg font-black tracking-tight text-slate-900 leading-none truncate max-w-[100px] sm:max-w-none">{t.brand}</h1>
            <p className="text-[8px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5 sm:mt-1">{t.subtitle}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden md:flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white shadow-sm rounded-lg active:scale-95 transition-transform">{t.editor}</button>
            <button className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors active:scale-95">{t.history}</button>
          </div>

          {/* Pricing Button */}
          <button 
            onClick={()=> setIsPricingOpen(true)} 
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-[10px] sm:text-xs font-bold rounded-xl shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 transition-all active:scale-95 shrink-0"
          >
            <SafeIcon icon={FiCreditCard} size={14} className="sm:size-[16px]" />
            <span className="whitespace-nowrap">{t.pricing}</span>
          </button>

          <div className="flex items-center gap-1 bg-slate-100 p-0.5 sm:p-1 rounded-lg sm:rounded-xl border border-slate-200 shrink-0">
            <button onClick={()=> setLang('jp')} className={`px-1.5 sm:px-2 py-1 text-[9px] sm:text-[10px] font-bold rounded-md sm:rounded-lg transition-all active:scale-95 ${lang==='jp' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>JP</button>
            <button onClick={()=> setLang('en')} className={`px-1.5 sm:px-2 py-1 text-[9px] sm:text-[10px] font-bold rounded-md sm:rounded-lg transition-all active:scale-95 ${lang==='en' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>EN</button>
          </div>

          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all hidden md:block active:scale-95 shrink-0">
            <SafeIcon icon={FiSettings} size={20} />
          </button>
        </div>
      </div>
    </nav>

    <main className="max-w-7xl mx-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pb-[100px] lg:pb-8">
      <div className="hidden lg:block lg:col-span-4 space-y-6 order-2 lg:order-1">
        {renderGallery('desktop')}
        <div className={`transition-all duration-500 ${images.length===0 ? 'opacity-40 pointer-events-none grayscale' : 'opacity-100'} space-y-6`}>
          {renderTools()}
          {renderAdjust()}
          {renderExport()}
        </div>
      </div>
      <div className="lg:col-span-8 flex flex-col order-1 lg:order-2 lg:sticky lg:top-24 z-40 self-start">
        <div className={`relative w-full rounded-[2.5rem] overflow-hidden bg-slate-100 border-4 border-white shadow-2xl transition-all duration-500 flex flex-col ${images.length===0 ? 'h-[400px]' : 'h-[60vh] md:h-[65vh] lg:max-h-none lg:h-[780px]'}`}>
          <input type="file" accept="image/*" multiple className="hidden" ref={fileInputRef} onChange={handleImageUpload} />

          {/* Mobile Top Bar */}
          {currentImageObj && (
            <div className="lg:hidden flex-none flex justify-between items-center px-4 py-3 bg-white border-b border-slate-100 z-10 shadow-sm">
              <div className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-xl flex items-center gap-2">
                <SafeIcon icon={FiMaximize} size={12} className="opacity-60" />
                <span className="text-[10px] font-black font-mono">{currentImageObj.width} × {currentImageObj.height}</span>
              </div>
              <button onPointerDown={(e)=> e.stopPropagation()} onClick={(e)=> {e.stopPropagation();toggleShowOriginal();}} className={`h-9 px-4 rounded-xl flex items-center gap-2 font-bold text-xs transition-all shadow-sm border active:scale-95 ${showOriginal ? 'bg-emerald-500 text-white border-emerald-400 ring-2 ring-emerald-400 ring-offset-1' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`} >
                <SafeIcon icon={showOriginal ? FiEyeOff : FiEye} size={14} />
                {showOriginal ? t.baseImage : t.compare}
              </button>
            </div>
          )}

          {/* Canvas Area */}
          <div className={`flex-1 relative overflow-hidden transition-all touch-none flex items-center justify-center ${cursorClass}`} onPointerDown={handleContainerPointerDown} onPointerMove={handleContainerPointerMove} onPointerUp={handleContainerPointerUp} onPointerLeave={handleContainerPointerUp} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onWheel={handleWheel} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={()=> {if (!currentImageObj) fileInputRef.current?.click();}} >
            <AnimatePresence>
              {isProcessing && currentImageObj && (
                <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} transition={{duration: 0.15}} className="absolute inset-0 z-[80] flex items-center justify-center bg-slate-900/20 backdrop-blur-[2px]" >
                  <div className="bg-white/95 backdrop-blur-xl px-8 py-6 rounded-[2rem] shadow-2xl border border-white flex flex-col items-center gap-5">
                    <div className="relative flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full border-4 border-slate-100"></div>
                      <div className="absolute w-14 h-14 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
                      <SafeIcon icon={BsMagic} size={18} className="absolute text-indigo-600 animate-pulse" />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-black text-slate-800 tracking-tight">{t.processing}</span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.pleaseWait}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {currentImageObj ? (
              <>
                {/* Desktop Overlays */}
                <div className="hidden lg:flex absolute top-6 left-6 right-6 justify-between items-start z-[60] pointer-events-none">
                  <div className="flex flex-col gap-2 pointer-events-auto" onPointerDown={(e)=> e.stopPropagation()} >
                    <div className="px-4 py-2 bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-white flex items-center gap-3">
                      <span className="text-xs font-black text-slate-800 uppercase tracking-tight">{t.preview}</span>
                    </div>
                    <div className="px-3 py-1.5 bg-slate-900/80 backdrop-blur-md text-white rounded-xl shadow-lg border border-white/10 flex items-center gap-2 transition-all hover:bg-slate-900">
                      <SafeIcon icon={FiMaximize} size={12} className="opacity-60" />
                      <span className="text-[10px] font-black font-mono">{currentImageObj.width} × {currentImageObj.height}</span>
                    </div>
                  </div>
                  <button onPointerDown={(e)=> e.stopPropagation()} onClick={(e)=> {e.stopPropagation();toggleShowOriginal();}} className={`pointer-events-auto h-11 px-5 rounded-2xl flex items-center gap-2 font-bold text-sm transition-all shadow-xl border active:scale-95 hover:scale-105 ${showOriginal ? 'bg-emerald-500 text-white border-emerald-400 ring-4 ring-emerald-400/30' : 'bg-white/90 backdrop-blur-md text-slate-700 border-white hover:bg-white'}`} >
                    <SafeIcon icon={showOriginal ? FiEyeOff : FiEye} size={16} />
                    {showOriginal ? t.baseImage : t.compare}
                  </button>
                </div>

                <AnimatePresence>
                  {showOriginal && (
                    <motion.div initial={{opacity: 0,y: -10}} animate={{opacity: 1,y: 0}} exit={{opacity: 0,y: -10}} className="absolute top-6 left-1/2 -translate-x-1/2 z-[65] lg:hidden pointer-events-none" >
                      <div className="px-4 py-2 bg-emerald-500 text-white rounded-2xl shadow-xl flex items-center gap-2 text-xs font-black uppercase tracking-wider">
                        <SafeIcon icon={FiEye} size={14} />
                        {t.viewingOriginal}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Desktop Zoom Controls */}
                <div onPointerDown={(e)=> e.stopPropagation()} onWheel={(e)=> e.stopPropagation()} className="hidden lg:flex absolute bottom-6 right-6 z-[60] pointer-events-auto items-center gap-1 bg-white/90 backdrop-blur-md p-1.5 rounded-2xl shadow-2xl border border-white" >
                  <button onClick={(e)=> {e.stopPropagation();handleZoomOut();}} className="w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-all active:scale-90" >
                    <SafeIcon icon={FiZoomOut} size={18} />
                  </button>
                  <div className="px-3 min-w-[70px] text-center">
                    <span className="text-[11px] font-black text-slate-900 font-mono">{Math.round(zoom * 100)}%</span>
                  </div>
                  <button onClick={(e)=> {e.stopPropagation();handleZoomIn();}} className="w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-all active:scale-90" >
                    <SafeIcon icon={FiZoomIn} size={18} />
                  </button>
                  <div className="w-px h-6 bg-slate-200 mx-1" />
                  <button onClick={(e)=> {e.stopPropagation();handleResetZoom();}} className="w-10 h-10 rounded-xl text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-all active:scale-90 hover:rotate-180 duration-500" >
                    <SafeIcon icon={FiRotateCcw} size={16} />
                  </button>
                </div>

                {/* Desktop Left/Right Navigation */}
                {images.length > 1 && (
                  <>
                    <button onPointerDown={(e)=> e.stopPropagation()} onClick={(e)=> {e.stopPropagation();handleSelectImage(currentIndex - 1);}} disabled={currentIndex===0} className="pointer-events-auto hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 bg-white/90 backdrop-blur-md text-slate-700 rounded-2xl shadow-xl items-center justify-center border border-white disabled:opacity-0 transition-all hover:scale-110 active:scale-90 hover:bg-white" >
                      <SafeIcon icon={FiChevronLeft} size={24} />
                    </button>
                    <button onPointerDown={(e)=> e.stopPropagation()} onClick={(e)=> {e.stopPropagation();handleSelectImage(currentIndex + 1);}} disabled={currentIndex===images.length - 1} className="pointer-events-auto hidden lg:flex absolute right-6 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 bg-white/90 backdrop-blur-md text-slate-700 rounded-2xl shadow-xl items-center justify-center border border-white disabled:opacity-0 transition-all hover:scale-110 active:scale-90 hover:bg-white" >
                      <SafeIcon icon={FiChevronRight} size={24} />
                    </button>
                  </>
                )}

                {/* Image Canvas Container */}
                <div className="relative shadow-2xl rounded-2xl overflow-hidden pointer-events-auto transition-all duration-300" style={{...(showOriginal ? originalBgStyle : checkerboardStyle),display: 'inline-block',maxWidth: '92%',maxHeight: '92%',aspectRatio: `${currentImageObj.width} / ${currentImageObj.height}`,transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,transformOrigin: 'center',transition: isPanning.current ? 'none' : 'transform 0.15s cubic-bezier(0.2,0.8,0.2,1)',outline: showOriginal ? '3px solid #10b981' : 'none',outlineOffset: '2px'}} onMouseEnter={()=> {if (!showOriginal) setShowCursor(true);}} onMouseLeave={()=> {stopDrawing();setShowCursor(false);}} >
                  <img src={currentImageObj.src} style={{maxWidth: '100%',maxHeight: '100%',width: 'auto',height: 'auto',visibility: 'hidden',pointerEvents: 'none',display: 'block'}} alt="" />
                  <canvas ref={canvasRef} className="w-full h-full absolute inset-0 transition-opacity duration-300 pointer-events-none opacity-100" />
                  <canvas ref={eraserCanvasRef} onPointerDown={handleCanvasPointerDown} onPointerMove={handleCanvasPointerMove} onPointerUp={stopDrawing} onPointerLeave={stopDrawing} style={{touchAction: (mode==='eraser' || mode==='picker') && !showOriginal ? 'none' : 'auto'}} className={`w-full h-full absolute inset-0 transition-opacity duration-300 ${showOriginal ? 'opacity-0 pointer-events-none' : 'opacity-80'} ${!showOriginal && (mode==='eraser' || mode==='picker') ? 'pointer-events-auto' : 'pointer-events-none'}`} />

                  {mode==='picker' && showCursor && !showOriginal && (
                    <div className="pointer-events-none absolute z-[70] transform -translate-x-1/2 -translate-y-[150%] bg-indigo-600 text-white text-[10px] font-black px-3 py-1.5 rounded-xl shadow-2xl flex items-center gap-2 whitespace-nowrap" style={{left: cursorPos.x + 'px',top: cursorPos.y + 'px'}} >
                      <SafeIcon icon={BsEyedropper} size={12} /> {t.eyeDropper.toUpperCase()}
                    </div>
                  )}

                  {mode==='eraser' && showCursor && !showOriginal && (
                    <div className="pointer-events-none absolute z-[70] rounded-full border-2 border-white/80 bg-indigo-500/20 shadow-lg backdrop-blur-[1px]" style={{width: (eraserSize / cursorPos.scale) + 'px',height: (eraserSize / cursorPos.scale) + 'px',left: cursorPos.x + 'px',top: cursorPos.y + 'px',transform: 'translate(-50%,-50%)'}} />
                  )}

                  <AnimatePresence>
                    {showOriginal && (
                      <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="absolute inset-0 pointer-events-none flex items-end justify-center pb-4" >
                        <div className="px-4 py-2 bg-emerald-500/90 backdrop-blur-md text-white rounded-xl shadow-lg text-[11px] font-black uppercase tracking-wider flex items-center gap-2">
                          <SafeIcon icon={FiEye} size={14} /> {t.baseImage}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-8 group cursor-pointer active:scale-95 transition-transform">
                <div className="w-24 h-24 bg-white rounded-[2rem] flex items-center justify-center shadow-2xl shadow-indigo-100 text-indigo-500 mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 group-active:scale-90 group-active:shadow-md">
                  <SafeIcon icon={FiUpload} size={40} />
                </div>
                <h2 className="text-xl font-black text-slate-800 tracking-tight mb-2">{t.dropZoneTitle}</h2>
                <p className="text-sm text-slate-400 font-medium max-w-[240px]">{t.dropZoneDesc}</p>
                <div className="mt-8 flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl font-bold text-xs transition-colors group-hover:bg-indigo-100">
                  <SafeIcon icon={FiCommand} size={14} />
                  <span>{t.clickBrowse}</span>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Bottom Bar */}
          {currentImageObj && (
            <div className="lg:hidden flex-none flex justify-between items-center px-4 py-3 bg-white border-t border-slate-100 z-10 shadow-[0_-2px_10px_rgba(0,0,0,0.02)]">
              <button onClick={()=> handleSelectImage(currentIndex - 1)} disabled={currentIndex===0 || images.length <=1} className="w-10 h-10 rounded-xl bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-30 active:scale-90 transition-transform" ><SafeIcon icon={FiChevronLeft} /></button>
              <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-xl">
                <button onClick={handleZoomOut} className="w-9 h-9 flex items-center justify-center text-slate-600 rounded-lg hover:bg-slate-200 active:scale-90 transition-all"><SafeIcon icon={FiZoomOut} size={16} /></button>
                <span className="text-xs font-black font-mono w-14 text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={handleZoomIn} className="w-9 h-9 flex items-center justify-center text-slate-600 rounded-lg hover:bg-slate-200 active:scale-90 transition-all"><SafeIcon icon={FiZoomIn} size={16} /></button>
              </div>
              <button onClick={()=> handleSelectImage(currentIndex + 1)} disabled={currentIndex===images.length - 1 || images.length <=1} className="w-10 h-10 rounded-xl bg-slate-50 text-slate-600 flex items-center justify-center disabled:opacity-30 active:scale-90 transition-transform" ><SafeIcon icon={FiChevronRight} /></button>
            </div>
          )}
        </div>
      </div>
    </main>

    <footer className="hidden lg:flex max-w-7xl mx-auto px-8 py-6 justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-widest border-t border-slate-200/50 mt-12">
      <div className="flex gap-6">
        <span className="hover:text-slate-600 transition-colors cursor-pointer">© 2024 LINE Studio</span>
        <span className="hover:text-slate-600 transition-colors cursor-pointer">Privacy Policy</span>
        <span className="hover:text-slate-600 transition-colors cursor-pointer">Terms</span>
      </div>
      <div className="flex gap-4">
        <div className="flex items-center gap-1.5">
          <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-slate-600 shadow-sm">←→</kbd>
          <span>Navigate</span>
        </div>
        <div className="flex items-center gap-1.5">
          <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-slate-600 shadow-sm">Wheel</kbd>
          <span>{t.zoom}</span>
        </div>
      </div>
    </footer>

    {/* Mobile Bottom Tab Bar */}
    <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-200/60 z-[80] px-2 py-2 pb-4 shadow-[0_-10px_40px_rgba(0,0,0,0.05)]">
      <div className="flex justify-around items-center max-w-md mx-auto">
        {[
          {id: 'gallery',icon: FiImage,label: t.gallery},{id: 'tools',icon: BsScissors,label: t.editor},{id: 'adjust',icon: FiSliders,label: t.advanced},{id: 'export',icon: FiDownload,label: t.export}
        ].map(tab=> (
          <button key={tab.id} onClick={()=> setActiveSheet(activeSheet===tab.id ? null : tab.id)} className={`flex flex-col items-center justify-center w-16 py-2 rounded-2xl transition-all active:scale-95 ${activeSheet===tab.id ? 'text-indigo-600 bg-indigo-50 shadow-inner ring-1 ring-indigo-100' : 'text-slate-400 hover:text-slate-600'}`} >
            <SafeIcon icon={tab.icon} size={20} className={`mb-1 transition-transform ${activeSheet===tab.id ? 'scale-110' : ''}`} />
            <span className="text-[9px] font-bold uppercase tracking-wider">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>

    {/* Mobile Tool Mode Bar */}
    <AnimatePresence>
      {mode !=='none' && window.innerWidth < 1024 && !activeSheet && (
        <motion.div initial={{y: 50,opacity: 0}} animate={{y: 0,opacity: 1}} exit={{y: 50,opacity: 0}} className="lg:hidden fixed bottom-[84px] left-4 right-4 bg-slate-900/95 backdrop-blur-md rounded-2xl p-3 z-[75] shadow-2xl border border-white/10 flex flex-col gap-3" >
          <div className="flex items-center justify-between gap-3">
            {mode==='eraser' ? (
              <>
                <div className="flex items-center gap-1">
                  <button onClick={handleUndo} disabled={!images[currentIndex]?.historyStep} className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center disabled:opacity-30 active:scale-90 transition-transform">
                    <SafeIcon icon={FiCornerUpLeft} size={18} />
                  </button>
                  <button onClick={handleRedo} disabled={images[currentIndex]?.historyStep >=images[currentIndex]?.history.length - 1} className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center disabled:opacity-30 active:scale-90 transition-transform">
                    <SafeIcon icon={FiCornerUpRight} size={18} />
                  </button>
                </div>
                <div className="flex-1 flex items-center gap-3 px-1">
                  <SafeIcon icon={BsEraser} size={14} className="text-white/50" />
                  <input type="range" min={5} max={150} value={eraserSize} onChange={(e)=> setEraserSize(Number(e.target.value))} className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-indigo-400 active:scale-[0.99]" />
                </div>
                <button onClick={()=> setMode('none')} className="h-9 px-4 bg-indigo-500 text-white text-[10px] font-black rounded-xl uppercase tracking-wider shadow-lg active:scale-95"> {t.done} </button>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-between px-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500 rounded-lg text-white">
                    <SafeIcon icon={BsEyedropper} size={16} />
                  </div>
                  <span className="text-white text-xs font-bold">{t.eyeDropper} {t.auto}</span>
                </div>
                <button onClick={()=> setMode('none')} className="h-9 px-4 bg-white/20 text-white text-[10px] font-black rounded-xl uppercase tracking-wider active:scale-95"> {t.cancel} </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Mobile Bottom Sheet */}
    <AnimatePresence>
      {activeSheet && (
        <>
          <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="lg:hidden fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[90]" onClick={()=> setActiveSheet(null)} />
          <motion.div initial={{y: '100%'}} animate={{y: 0}} exit={{y: '100%'}} transition={{type: 'spring',damping: 25,stiffness: 200}} className="lg:hidden fixed bottom-[72px] left-0 right-0 bg-[#fcfcfd] rounded-t-[2rem] z-[100] shadow-2xl border-t border-white" style={{maxHeight: 'calc(85vh - 72px)'}} >
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mt-4 mb-2" />
            <div className="p-5 overflow-y-auto" style={{maxHeight: 'calc(85vh - 100px)'}}>
              {activeSheet==='gallery' && renderGallery('mobile')}
              {activeSheet==='tools' && renderTools()}
              {activeSheet==='adjust' && renderAdjust()}
              {activeSheet==='export' && renderExport()}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>

    {/* Toast Notification */}
    <AnimatePresence>
      {toast && (
        <motion.div initial={{opacity: 0,y: 50,scale: 0.9}} animate={{opacity: 1,y: 0,scale: 1}} exit={{opacity: 0,scale: 0.9,transition: {duration: 0.2}}} className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] max-w-sm w-[90%]" >
          <div className="bg-slate-900 text-white p-5 rounded-[2.5rem] shadow-2xl border border-white/10 flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/20">
                <SafeIcon icon={FiCheck} size={20} className="text-white" />
              </div>
              <span className="font-black text-sm">{toast}</span>
            </div>
            <div className="flex gap-2 pl-12">
              <SafeIcon icon={FiInfo} size={14} className="text-slate-400 shrink-0 mt-0.5" />
              <p className="text-[10px] text-slate-400 font-bold leading-relaxed">{t.saveHint}</p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Single Save Modal */}
    <AnimatePresence>
      {isSaveModalOpen && (
        <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-[110] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=> setIsSaveModalOpen(false)} >
          <motion.div initial={{scale: 0.9,y: 20}} animate={{scale: 1,y: 0}} exit={{scale: 0.9,y: 20}} className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-slate-100" onClick={(e)=> e.stopPropagation()} >
            <h3 className="text-lg font-black text-slate-800 mb-4">{t.fileNameModalTitle}</h3>
            <div className="flex items-center bg-slate-100 rounded-xl px-4 py-3 mb-4">
              <input type="text" value={customFileName} onChange={(e)=> setCustomFileName(e.target.value)} className="flex-1 bg-transparent text-slate-800 font-bold text-sm outline-none" placeholder={t.fileNamePlaceholder} autoFocus />
              <span className="text-slate-400 font-bold text-sm">.png</span>
            </div>
            <div className="mb-6 px-1">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${useSavePicker ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 group-hover:border-indigo-400'}`}>
                  {useSavePicker && <SafeIcon icon={FiCheck} size={12} className="text-white" />}
                </div>
                <input type="checkbox" className="hidden" checked={useSavePicker} onChange={(e)=> setUseSavePicker(e.target.checked)} />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight group-hover:text-slate-700 transition-colors"> {t.saveAsChoice} </span>
              </label>
            </div>
            <div className="flex gap-3">
              <button onClick={()=> setIsSaveModalOpen(false)} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-all active:scale-95"> {t.cancel} </button>
              <button onClick={confirmDownloadSingle} className="flex-1 h-11 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-500 transition-all active:scale-95 shadow-md shadow-indigo-500/30"> {t.save} </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Batch Save Modal */}
    <AnimatePresence>
      {isBatchSaveModalOpen && (
        <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="fixed inset-0 z-[110] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=> setIsBatchSaveModalOpen(false)} >
          <motion.div initial={{scale: 0.9,y: 20}} animate={{scale: 1,y: 0}} exit={{scale: 0.9,y: 20}} className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-slate-100" onClick={(e)=> e.stopPropagation()} >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                <SafeIcon icon={FiLayers} size={18} />
              </div>
              <h3 className="text-lg font-black text-slate-800">{t.batchFileNameModalTitle}</h3>
            </div>
            <div className="space-y-4 mb-6">
              <div className="flex items-center bg-slate-100 rounded-xl px-4 py-3">
                <input type="text" value={batchFileName} onChange={(e)=> setBatchFileName(e.target.value)} className="flex-1 bg-transparent text-slate-800 font-bold text-sm outline-none" placeholder="stamp" autoFocus />
                <span className="text-slate-400 font-bold text-sm">_1.png ...</span>
              </div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{t.numberSuffix}</p>
              <div className="flex gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <SafeIcon icon={FiInfo} size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-700 font-bold leading-relaxed">一括保存は「ダウンロード」フォルダに保存されます。ブラウザの設定で保存先を確認してください。</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={()=> setIsBatchSaveModalOpen(false)} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-all active:scale-95"> {t.cancel} </button>
              <button onClick={confirmDownloadBatch} className="flex-1 h-11 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-500 transition-all active:scale-95 shadow-md shadow-indigo-500/30"> {t.save} ({images.length}) </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Pricing Modal */}
    <PricingModal isOpen={isPricingOpen} onClose={()=> setIsPricingOpen(false)} lang={lang} />
  </div>
);
}